/**
 * FindingVerdictBar
 *
 * Scope Intelligence status bar — color + status line from panel verdict.
 *
 * @module @ideoz/ui/components/molecules/FindingVerdictBar
 */

import React from 'react';
import { cn } from '../../lib/utils';
import type { FindingVerdictBarProps } from '../../types/Findings.types';

const colorClasses: Record<string, string> = {
  green: 'bg-success-100 text-success-800 border-success-200',
  amber: 'bg-warning-50 text-warning-800 border-warning-200',
  red: 'bg-error-50 text-error-800 border-error-200',
};

const dotClasses: Record<string, string> = {
  green: 'bg-success-500',
  amber: 'bg-warning-500',
  red: 'bg-error-500',
};

/**
 * FindingVerdictBar Component
 *
 * Pure presentation — verdict derived server-side; never set by the client.
 */
export const FindingVerdictBar: React.FC<FindingVerdictBarProps> = ({
  verdict,
  className = '',
  testId = 'finding-verdict-bar',
}) => {
  const color = verdict.color || 'amber';

  return (
    <div
      data-testid={testId}
      className={cn(
        'flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs',
        colorClasses[color] ?? colorClasses.amber,
        className
      )}
    >
      <span
        className={cn('h-2 w-2 shrink-0 rounded-full', dotClasses[color] ?? dotClasses.amber)}
        aria-hidden
      />
      <span className="min-w-0 flex-1 truncate font-medium" title={verdict.status_line}>
        {verdict.status_line}
      </span>
      <span className="shrink-0 opacity-70">{verdict.reviewed_count} reviewed</span>
    </div>
  );
};

export default FindingVerdictBar;
